import { create } from 'zustand';
import type {
  VoiceAgentState,
  VoiceCallStatus,
  VoiceCaption,
} from 'features/chat/types/voice-call.types';

/**
 * Serializable voice-call UI state. The LiveKit room itself lives in
 * `features/chat/services/voice-call-room.ts`; this store only mirrors what the UI shows,
 * so the call survives the `/chat` → `/chat/:conversationId` remount.
 */
interface VoiceCallState {
  status: VoiceCallStatus;
  agentState: VoiceAgentState;
  agentId: string | null;
  conversationId: string | null;
  roomName: string | null;
  isMuted: boolean;
  isMinimized: boolean;
  /** Set after a 503 `voice_not_configured`; hides the Call button for this session. */
  unavailable: boolean;
  agentCaption: VoiceCaption | null;
  userCaption: VoiceCaption | null;
  startedAt: number | null;

  beginRequest: (agentId: string) => void;
  setSession: (session: { conversationId: string; roomName: string }) => void;
  setStatus: (status: VoiceCallStatus) => void;
  setAgentState: (agentState: VoiceAgentState) => void;
  setMuted: (isMuted: boolean) => void;
  setMinimized: (isMinimized: boolean) => void;
  setUnavailable: (unavailable: boolean) => void;
  upsertCaption: (caption: VoiceCaption) => void;
  reset: () => void;
}

const initialCallState = {
  status: 'idle' as VoiceCallStatus,
  agentState: 'initializing' as VoiceAgentState,
  agentId: null,
  conversationId: null,
  roomName: null,
  isMuted: false,
  isMinimized: false,
  agentCaption: null,
  userCaption: null,
  startedAt: null,
};

export const useVoiceCallStore = create<VoiceCallState>((set) => ({
  ...initialCallState,
  unavailable: false,

  beginRequest: (agentId) =>
    set({ ...initialCallState, status: 'requesting', agentId, isMinimized: true }),
  setSession: ({ conversationId, roomName }) =>
    set({ status: 'connecting', conversationId, roomName, startedAt: Date.now() }),
  setStatus: (status) => set({ status }),
  setAgentState: (agentState) => set({ agentState }),
  setMuted: (isMuted) => set({ isMuted }),
  setMinimized: (isMinimized) => set({ isMinimized }),
  setUnavailable: (unavailable) => set({ unavailable }),
  upsertCaption: (caption) =>
    set(
      caption.who === 'agent'
        ? { agentCaption: caption }
        : // A new user turn starts a new exchange, so the previous answer is cleared.
          { userCaption: caption, agentCaption: null }
    ),
  reset: () => set({ ...initialCallState }),
}));
